import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Upload, X, Image, Loader2, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PhotoGallery({ leadId, canUpload = false }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPhotos = () => {
    axios.get(`${API}/deals/${leadId}/photos`, { headers })
      .then(r => setPhotos(r.data.photos || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPhotos(); }, [leadId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", file.name);
      try {
        await axios.post(`${API}/deals/${leadId}/photos`, fd, { headers });
      } catch {}
    }
    setUploading(false);
    fetchPhotos();
    if (fileRef.current) fileRef.current.value = "";
  };

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const prevPhoto = () => setLightbox(i => (i > 0 ? i - 1 : photos.length - 1));
  const nextPhoto = () => setLightbox(i => (i < photos.length - 1 ? i + 1 : 0));

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#888]" /></div>;

  return (
    <div data-testid="photo-gallery">
      {/* Upload Button */}
      {canUpload && (
        <div className="mb-4">
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[#ccc] text-sm text-[#666] hover:border-blue-400 hover:text-blue-600 cursor-pointer transition-colors">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload Photos"}
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} data-testid="photo-upload-input" />
          </label>
        </div>
      )}

      {/* Gallery Grid */}
      {photos.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#ddd] bg-gray-50/50 p-8 text-center">
          <Image className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
          <p className="text-sm text-[#888]">No photos uploaded yet</p>
          {canUpload && <p className="text-xs text-[#aaa] mt-1">Upload property photos, rehab progress, or site images</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((p, i) => (
            <div key={p.doc_id} className="group relative aspect-square rounded-lg overflow-hidden border border-[#e8e0d0] cursor-pointer hover:border-blue-400 transition-colors" onClick={() => openLightbox(i)}>
              <img src={p.storage_url} alt={p.description || p.filename} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                <p className="text-[10px] text-white truncate">{p.description || p.filename}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox !== null && photos[lightbox] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="h-6 w-6" /></button>
          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prevPhoto(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 rounded-full p-2"><ChevronLeft className="h-6 w-6" /></button>
              <button onClick={(e) => { e.stopPropagation(); nextPhoto(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-black/30 rounded-full p-2"><ChevronRight className="h-6 w-6" /></button>
            </>
          )}
          <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[85vh]">
            <img src={photos[lightbox].storage_url} alt={photos[lightbox].description} className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            <div className="text-center mt-3">
              <p className="text-white/90 text-sm">{photos[lightbox].description || photos[lightbox].filename}</p>
              <p className="text-white/50 text-xs mt-1">Uploaded by {photos[lightbox].uploaded_by_name} ({photos[lightbox].uploaded_by_role})</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
